import { useState, type ReactNode } from 'react';
import { Alert, Button, Drawer, Space, Switch, Tag, Typography, message, theme } from 'antd';
import {
    DeploymentUnitOutlined,
    FilePdfOutlined,
    InboxOutlined,
    ThunderboltOutlined,
    ToolOutlined,
} from '@ant-design/icons';
import type { Tenant, TenantFeatures } from 'breuer-supercharged-sdk';
import { useUpdateTenant } from '@/hooks/useSystemAdminApi';
import { describeApiError } from '@/utils/api_error_message';
import { TENANT_MODULES, isModuleEnabled, sameFeatureSet, setTenantFeature } from './tenant_modules';

const { Text } = Typography;

const MODULE_ICONS: Record<string, ReactNode> = {
    'stücklisten_extractor': <ThunderboltOutlined />,
    offer_generation: <FilePdfOutlined />,
    steel_mind: <DeploymentUnitOutlined />,
    order_inbox: <InboxOutlined />,
    arbeitsvorbereitung: <ToolOutlined />,
};

const SWITCHABLE_COUNT = TENANT_MODULES.filter((m) => m.feature !== null).length;

interface TenantModulesDrawerProps {
    tenant: Tenant;
    open: boolean;
    onClose: () => void;
}

/** Module editor of one tenant. Remount (new `key`) per opening so the draft starts from the saved state. */
export default function TenantModulesDrawer({ tenant, open, onClose }: TenantModulesDrawerProps) {
    const { token } = theme.useToken();
    const [features, setFeatures] = useState<TenantFeatures[]>(tenant.features ?? []);
    const updateTenantMutation = useUpdateTenant();
    const isDirty = !sameFeatureSet(features, tenant.features);
    const enabledCount = TENANT_MODULES.filter((m) => m.feature !== null && isModuleEnabled(features, m)).length;

    const handleSave = async () => {
        try {
            await updateTenantMutation.mutateAsync({ tenantId: tenant.id, tenantData: { features } });
            message.success(`Module für ${tenant.name} gespeichert`);
            onClose();
        } catch (error) {
            console.error('Error saving tenant modules:', error);
            message.error(describeApiError('Fehler beim Speichern der Module', error));
        }
    };

    return (
        <Drawer
            title={`Module – ${tenant.name}`}
            open={open}
            onClose={onClose}
            width={520}
            footer={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary">
                        {enabledCount} von {SWITCHABLE_COUNT} Modulen aktiv
                    </Text>
                    <Space>
                        <Button onClick={onClose}>Abbrechen</Button>
                        <Button
                            type="primary"
                            onClick={handleSave}
                            disabled={!isDirty}
                            loading={updateTenantMutation.isPending}
                        >
                            Speichern
                        </Button>
                    </Space>
                </div>
            }
        >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Text type="secondary">
                    Legen Sie fest, welche Module für diesen Mandanten freigeschaltet sind. Nur Systemadministratoren
                    können Module schalten.
                </Text>
                <Alert
                    type="info"
                    showIcon
                    message="Benutzer des Mandanten sehen die Änderung ohne erneute Anmeldung – spätestens beim nächsten Wechsel in das Browserfenster."
                />
                {TENANT_MODULES.map((module) => {
                    const alwaysOn = module.feature === null;
                    const enabled = isModuleEnabled(features, module);
                    return (
                        <div
                            key={module.key}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 14,
                                padding: '12px 16px',
                                borderRadius: token.borderRadiusLG,
                                border: `1px solid ${enabled ? token.colorPrimaryBorder : token.colorBorderSecondary}`,
                                background: token.colorBgContainer,
                                transition: 'border-color 0.2s',
                            }}
                        >
                            <div
                                style={{
                                    flex: '0 0 36px',
                                    height: 36,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: token.borderRadius,
                                    fontSize: 18,
                                    color: enabled ? token.colorPrimary : token.colorTextTertiary,
                                    background: enabled ? token.colorPrimaryBg : token.colorFillTertiary,
                                }}
                            >
                                {MODULE_ICONS[module.key]}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <Space size={8} wrap>
                                    <Text strong>{module.label}</Text>
                                    {alwaysOn && <Tag>immer aktiv</Tag>}
                                </Space>
                                <div>
                                    <Text type="secondary" style={{ fontSize: 13 }}>
                                        {module.description}
                                    </Text>
                                </div>
                            </div>
                            <Switch
                                aria-label={module.label}
                                checked={enabled}
                                disabled={alwaysOn || updateTenantMutation.isPending}
                                onChange={(checked) => {
                                    const feature = module.feature;
                                    if (feature) setFeatures((current) => setTenantFeature(current, feature, checked));
                                }}
                            />
                        </div>
                    );
                })}
            </Space>
        </Drawer>
    );
}
